/* eslint-env browser */
import * as React from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentId } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';
import { Alert, Base, Button, Spinner, Svg } from '@gandi/ui';
import { usePrevious } from '../../../hooks/usePrevious';
import { selectPaymentCallParams } from '../../../modules/money';
import PaymentPlaceholder from '../../PaymentPlaceholder';
import { usePrepaid, usePaymentProvider } from '../hooks';
import { useCuratedPaymentMeans } from '../modules/hooks';
import AdyenDropIn from './AdyenDropIn';
import FlushPrepaidCheckbox from './FlushPrepaidCheckbox';
import OrderTotal from './OrderTotal';
import PaymentMethodChoice from './PaymentMethodChoice';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var styles = {
  "icon": "NewPaymentMethodForm-icon_24hJi"
};
const buildReturnUrl = (returnUrl, orderId) => {
  const url = new URL(returnUrl);
  if (typeof window !== 'undefined') {
    const retry = window.location.toString();
    url.searchParams.set('retry', retry);
  }
  url.searchParams.set('order', String(orderId));
  return url.href;
};
export default function NewPaymentForm({
  paymentMethodSave,
  redirectTo,
  order,
  amount,
  currency,
  onHandlePaymentCompleted,
  context: propContext,
  disableSubmit
}) {
  const {
    __,
    formatPrice
  } = useTranslator();
  const sharingId = useSelector(selectCurrentId);
  const prepaid = usePrepaid({
    sharingId
  });
  const payment = useSelector(selectPaymentCallParams);
  const [selectedMethod, setSelectedMethod] = React.useState(undefined);
  const [flushPrepaid, setFlushPrepaid] = React.useState(false);
  const [saveMethod, setSaveMethod] = React.useState(paymentMethodSave === 'mandatory');
  const [isAdyenPaying, setIsAdyenPaying] = React.useState(false);
  const [isAdyenComplete, setIsAdyenComplete] = React.useState(false);
  const [isDropinLoaded, setDropinLoaded] = React.useState(false);
  const [error, setError] = React.useState(null);
  const context = flushPrepaid ? 'flush' : propContext || 'pay';
  const {
    isGandiPaying,
    getProviderCallParams
  } = usePaymentProvider(context, order, buildReturnUrl(redirectTo, order?.id), sharingId, amount, currency);
  const isPaying = isGandiPaying || isAdyenPaying;
  const mayFlushPrepaid = prepaid && prepaid.amount > 0 && context !== 'refill' && (amount || order?.price_after_taxes || 0) > prepaid.amount;
  const isFree = order && order.price_after_taxes <= 0;
  const {
    means,
    emptyMeans,
    isFetching,
    isFailure,
    isLoaded,
    adyenDropinMean
  } = useCuratedPaymentMeans({
    context,
    currency: order?.currency,
    allowEmptyPrepaid: isFree ? 1 : 0
  });
  const dropinRef = React.useRef(null);
  const previousChoice = usePrevious(selectedMethod);
  const prepaidMean = means?.find(({
    key
  }) => key === 'prepaid');
  const deferredMean = means?.find(({
    key
  }) => key === 'deferred');
  const isFreeOrderError = isLoaded && isFree && !prepaidMean && !deferredMean;

  /* Handle free orders
   * it may be cart within product for free
   * or deferred payments
   * This case we auto submit the form
   */
  React.useEffect(() => {
    if (!isPaying && isFree && (prepaidMean || deferredMean)) {
      getProviderCallParams(deferredMean || prepaidMean, false);
    }
  }, [getProviderCallParams, isFree, prepaidMean, deferredMean, isPaying]);
  const handlePayClick = React.useCallback(() => getProviderCallParams(selectedMethod, saveMethod), [getProviderCallParams, selectedMethod, saveMethod]);
  React.useEffect(() => {
    if (!selectedMethod && means?.length === 1 && !adyenDropinMean) {
      setSelectedMethod(means[0]);
    }
  }, [adyenDropinMean, means, selectedMethod]);
  const handleMethodChange = React.useCallback(paymentMethod => {
    setSelectedMethod(paymentMethod);
    setSaveMethod(false);
  }, []);
  const handleError = err => setError(err);
  const handleSaveMethodChange = React.useCallback(shouldSaveMethod => {
    setSaveMethod(shouldSaveMethod);
  }, []);
  const handleAdyenSelect = React.useCallback(() => {
    setSelectedMethod(undefined);
    setSaveMethod(false);
  }, []);
  const handleAdyenPaymentEvent = React.useCallback(async event => {
    setIsAdyenPaying(['start', 'success'].includes(event));
    setIsAdyenComplete(['success', 'failed'].includes(event));
    if (onHandlePaymentCompleted) {
      await onHandlePaymentCompleted(event);
    }
  }, [onHandlePaymentCompleted]);
  const handleFlushToggle = React.useCallback(flush => {
    setFlushPrepaid(flush);
  }, [setFlushPrepaid]);
  const handleDropinLoaded = React.useCallback(() => {
    setDropinLoaded(true);
  }, [setDropinLoaded]);
  // choice/method is undefined when adyen mean is selected => shouldResetChoice is true when adyen was selected, then user chooses sth else
  const shouldResetChoice = previousChoice === undefined && selectedMethod !== undefined;
  if (payment && payment.url) {
    return /*#__PURE__*/_jsx(PaymentPlaceholder, {
      payment: payment
    });
  }

  // XXX: we may want a form in the future. For delay reasons, we chose not to use on for now.
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [(isFetching || !isDropinLoaded) && (!error && !emptyMeans || isFree && !isFreeOrderError) && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Base, {
        tagName: "p",
        marginTop: "none",
        weight: "semibold",
        children: __('Please wait...')
      }), /*#__PURE__*/_jsx(Base, {
        padding: "xsmall",
        margin: "small",
        children: /*#__PURE__*/_jsx(Spinner, {
          name: "dots",
          centered: true
        })
      })]
    }), (emptyMeans || error) && /*#__PURE__*/_jsx(Alert, {
      intent: "danger",
      marginBottom: "base",
      message: error?.message || __('Unable to retrieve suitable payment means. Please retry later.')
    }), (!Number.isNaN(amount) || order) && !emptyMeans && !isFetching && /*#__PURE__*/_jsxs(Base, {
      backgroundColor: "white",
      padding: "small",
      marginbottom: "base",
      borderRadius: "1x",
      children: [order && /*#__PURE__*/_jsx(OrderTotal, {
        currency: order.currency,
        priceAfterTaxes: order.price_after_taxes,
        priceBeforeTaxes: order.price_before_taxes,
        taxes: order.taxes
      }), !isFree && !emptyMeans && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Base, {
          tagName: "p",
          marginTop: "none",
          marginBottom: "small",
          weight: "semibold",
          children: __('Select your payment method')
        }), prepaid && mayFlushPrepaid && /*#__PURE__*/_jsx(FlushPrepaidCheckbox, {
          context: context,
          totalToPay: amount || order?.price_after_taxes || 0,
          onChange: handleFlushToggle
        }), order && adyenDropinMean && /*#__PURE__*/_jsx(AdyenDropIn, {
          adyenDropinMean,
          order,
          sharingId,
          context,
          dropinRef,
          returnUrl: buildReturnUrl(redirectTo, order.id),
          onAdyenPaymentEvent: handleAdyenPaymentEvent,
          onSelect: handleAdyenSelect,
          onReady: handleDropinLoaded,
          onError: handleError,
          shouldResetChoice: shouldResetChoice
        }), !isAdyenComplete && (adyenDropinMean && isDropinLoaded || !adyenDropinMean) && /*#__PURE__*/_jsx(PaymentMethodChoice, {
          currency: prepaid?.currency || order?.currency,
          disabled: isAdyenPaying,
          paymentMethods: means,
          prepaidAmount: prepaid?.amount,
          onChange: handleMethodChange,
          value: selectedMethod,
          paymentMethodSave: paymentMethodSave,
          saveMethod: saveMethod,
          onSaveMethodChange: handleSaveMethodChange,
          payBlock: /*#__PURE__*/_jsx(Base, {
            backgroundColor: "white",
            paddingX: "xsmall",
            marginBottom: "small",
            children: /*#__PURE__*/_jsx(Button, {
              type: "button",
              block: true,
              intent: "primary",
              disabled: !selectedMethod || disableSubmit,
              loading: !selectedMethod || isPaying || disableSubmit,
              onClick: handlePayClick,
              children: selectedMethod ?
              // $FlowFixMe[incompatible-call] either it's a refill and amount is defined, or it's a payment and order is defined
              __('Pay %(amountToPay)s', {
                amountToPay: amount ? formatPrice(Number(amount), currency) : order?.price_after_taxes_label
              }) : __('Pay')
            })
          })
        })]
      })]
    }), isFreeOrderError && /*#__PURE__*/_jsx(Alert, {
      intent: "danger",
      marginBottom: "base",
      message: __('You cannot proceed with this order on the selected organization. Even for a free order you should be allowed to use the prepaid account.')
    }), paymentMethodSave === 'mandatory' && !isFailure && !isFree && /*#__PURE__*/_jsxs(Base, {
      paddingTop: "xsmall",
      gap: "xsmall",
      flex: true,
      flexAlignItemsStart: true,
      marginBottom: "base",
      children: [/*#__PURE__*/_jsx(Base, {
        tagName: "span",
        children: /*#__PURE__*/_jsx(Svg, {
          name: "successWire",
          theme: "success",
          className: styles.icon
        })
      }), __('Your payment details will be safely stored at our payment gateway and you can delete or modify them at any time.')]
    })]
  });
}